import React, { Fragment, useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUserById, updateUser } from "../../api/user.api";
import { useTranslation } from "react-i18next";
import { H5, Breadcrumbs } from "../../AbstractElements";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { toast } from "react-toastify";

const UserEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    role: "operation",
    status: "active",
  });

  const generateUsername = (name) => {
    if (!name) return "";

    const parts = name.trim().toLowerCase().split(/\s+/);

    if (parts.length === 1) return parts[0];

    return parts[0] + parts[1].charAt(0);
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      if (field === "name") {
        updated.username = generateUsername(value);
      }

      return updated;
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getUserById(id);
        setForm({
          name: data.name || "",
          username: data.username || "",
          email: data.email || "",
          role: data.role || "",
          status: data.status || "active",
        });
      } catch (err) {
        toast.error(err.message);
        navigate("/users");
      }
    };
    load();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (
        !form.name ||
        !form.username ||
        !form.email ||
        !form.role
      ) {
        return toast.error("all fields are required");
      }

      setLoading(true);
      await updateUser(id, form);
      toast.success(t("user updated successfully"));
      navigate("/users");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const triggerSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("user"))}
        title={titleCase(t("edit user"))}
        mainTitle={titleCase(t("user management"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Card className="mb-4">
              <CardHeader>
                <H5>{titleCase(t("edit user"))}</H5>
              </CardHeader>

              <CardBody>
                <Form innerRef={formRef} onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label>{titleCase(t("name"))}</Label>
                    <Input
                      type="text"
                      value={form.name}
                      onChange={(e) =>
                        handleChange("name", e.target.value)
                      }
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("username"))}</Label>
                    <Input
                      type="text"
                      value={form.username}
                      onChange={(e) =>
                        handleChange("username", e.target.value)
                      }
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("email"))}</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) =>
                        handleChange("email", e.target.value)
                      }
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("role"))}</Label>
                    <Input
                      type="select"
                      value={form.role}
                      onChange={(e) =>
                        handleChange("role", e.target.value)
                      }
                    >
                      <option value="superadmin">
                        {titleCase(t("superadmin"))}
                      </option>

                      <option value="operation">
                        {titleCase(t("operation"))}
                      </option>

                      <option value="finance">
                        {titleCase(t("finance"))}
                      </option>

                      <option value="security">
                        {titleCase(t("security"))}
                      </option>
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("status"))}</Label>

                    <Input
                      type="select"
                      value={form.status}
                      onChange={(e) =>
                        handleChange("status", e.target.value)
                      }
                    >
                      <option value="active">
                        {titleCase(t("active"))}
                      </option>

                      <option value="inactive">
                        {titleCase(t("inactive"))}
                      </option>
                    </Input>
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default UserEdit;